(function(global){
  // Namespace for visualizer config validation utilities
  // Provide a shared helper to determine configurator type across modules
  function normalizeConfiguratorType(rawType) {
    const normalized = rawType == null ? '' : String(rawType).trim().toLowerCase()
    if (!normalized) return 'classic'
    const aliases = {
      bookcases: 'bookcases',
      carcasses: 'carcasses',
      wallunit: 'wall-unit',
      wall_unit: 'wall-unit',
      'wall-unit': 'wall-unit',
    }
    return aliases[normalized] || normalized
  }

  function getConfigTypeSafe() {
    try {
      const V = global.Visualizer || {}
      const t = V.configType || (V.isDoor ? 'door' : 'classic')
      return normalizeConfiguratorType(t)
    } catch (e) {
      return 'classic'
    }
  }

  function getDoorConfigReadinessIssues(cfg = {}) {
    const issues = []
    const has = (k) => cfg[k] !== undefined && cfg[k] !== null && cfg[k] !== ''
    const hasNumber = (k) => has(k) && !isNaN(Number(cfg[k])) && Number(cfg[k]) > 0

    // Determine if a valid color is present for door mode
    const hasUrl = (v) => typeof v === 'string' && (v.startsWith('/') || v.startsWith('http'))
    const hasDoorColor = (
      has('doorsColor') ||
      (has('doorsMaterialRange') && (hasUrl(cfg['doorsMaterialRange']) || (cfg['doorsMaterialRange'] === 'luxe' && has('doorsLuxeColor')))) ||
      (has('materialRange') && (hasUrl(cfg['materialRange']) || (cfg['materialRange'] === 'luxe' && has('luxeColor'))))
    )
    if (!hasDoorColor) issues.push('missing color')

    if (!hasNumber('dimensions-width')) issues.push('invalid dimensions-width (> 0 required)')
    if (!hasNumber('dimensions-height')) issues.push('invalid dimensions-height (> 0 required)')
    // In door mode, depth, mountType, baseType-type and doors-type are not required

    return issues
  }

  function getClassicConfigReadinessIssues(cfg = {}) {
    const issues = []
    const has = (k) => cfg[k] !== undefined && cfg[k] !== null && cfg[k] !== ''
    const hasNumber = (k) => has(k) && !isNaN(Number(cfg[k])) && Number(cfg[k]) > 0

    if (!has('color') && !has('luxeColor') && !(cfg['materialRange']?.startsWith('/') || cfg['materialRange']?.startsWith('http'))) issues.push('missing color')
    if (!has('mountType')) issues.push('missing mountType')
    if (!has('baseType-type')) issues.push('missing baseType-type')
    if (!hasNumber('dimensions-width')) issues.push('invalid dimensions-width (> 0 required)')
    if (!hasNumber('dimensions-height')) issues.push('invalid dimensions-height (> 0 required)')
    if (!hasNumber('dimensions-depth')) issues.push('invalid dimensions-depth (> 0 required)')
    if (!has('doors-type')) issues.push('missing doors-type')

    // Branch-specific checks for baseType
    if (cfg['baseType-type'] === 'legs') {
      if (!has('baseType-legCode')) issues.push('missing baseType-legCode (legs)')
      if (!has('baseType-color')) issues.push('missing baseType-color (legs)')
    }
    else if (cfg['baseType-type'] === 'plinth' && cfg['mountType'] === 'floor') {
      if (!hasNumber('baseType-plinth-height')) issues.push('invalid baseType-plinth-height (> 0 required) (plinth)')
    }

    return issues
  }

  // Wall-unit readiness: same as classic cupboards, but mount/base are forced in payload
  function getWallUnitConfigReadinessIssues(cfg = {}) {
    const issues = []
    const has = (k) => cfg[k] !== undefined && cfg[k] !== null && cfg[k] !== ''
    const hasNumber = (k) => has(k) && !isNaN(Number(cfg[k])) && Number(cfg[k]) > 0

    if (!has('color') && !has('luxeColor') && !(cfg['materialRange']?.startsWith('/') || cfg['materialRange']?.startsWith('http'))) issues.push('missing color')
    if (!hasNumber('dimensions-width')) issues.push('invalid dimensions-width (> 0 required)')
    if (!hasNumber('dimensions-height')) issues.push('invalid dimensions-height (> 0 required)')
    if (!hasNumber('dimensions-depth')) issues.push('invalid dimensions-depth (> 0 required)')
    if (!has('doors-type')) issues.push('missing doors-type')

    return issues
  }

  // Bookcases readiness: similar to classic but without door requirements (no doors or handles)
  function getBookcasesConfigReadinessIssues(cfg = {}) {
    const issues = []
    const has = (k) => cfg[k] !== undefined && cfg[k] !== null && cfg[k] !== ''
    const hasNumber = (k) => has(k) && !isNaN(Number(cfg[k])) && Number(cfg[k]) > 0

    if (!has('color') && !has('luxeColor') && !(cfg['materialRange']?.startsWith('/') || cfg['materialRange']?.startsWith('http'))) issues.push('missing color')
    if (!has('mountType')) issues.push('missing mountType')
    if (!has('baseType-type')) issues.push('missing baseType-type')
    if (!hasNumber('dimensions-width')) issues.push('invalid dimensions-width (> 0 required)')
    if (!hasNumber('dimensions-height')) issues.push('invalid dimensions-height (> 0 required)')
    if (!hasNumber('dimensions-depth')) issues.push('invalid dimensions-depth (> 0 required)')
    // No doors-type check for bookcases

    if (cfg['baseType-type'] === 'legs') {
      if (!has('baseType-legCode')) issues.push('missing baseType-legCode (legs)')
      if (!has('baseType-color')) issues.push('missing baseType-color (legs)')
    }
    else if (cfg['baseType-type'] === 'plinth' && cfg['mountType'] === 'floor') {
      if (!hasNumber('baseType-plinth-height')) issues.push('invalid baseType-plinth-height (> 0 required) (plinth)')
    }

    return issues
  }

  const Validation = {
    // Returns a list of issues with the provided config. If includeIframeCheck is true,
    // it will also add 'iframe not loaded' if isConfiguratorLoaded is falsy.
    getConfigReadinessIssues(cfg = {}, opts = {}) {
      const { includeIframeCheck = true, isConfiguratorLoaded = false } = opts || {}
      const type = getConfigTypeSafe()
      const cfgForChecks = (() => {
        if (type !== 'carcasses') return cfg
        const baseType = (cfg?.['baseType-type'] ?? '').toString().trim().toLowerCase()
        if (baseType !== 'legs') return cfg
        // Carcasses with legs always use kitchen leg code in payload.
        if (cfg?.['baseType-legCode']) return cfg
        return { ...cfg, 'baseType-legCode': 'kitchen' }
      })()
      let issues = []
      if (type === 'door') issues = getDoorConfigReadinessIssues(cfgForChecks)
      else if (type === 'wall-unit') issues = getWallUnitConfigReadinessIssues(cfgForChecks)
      else if (type === 'bookcases' || type === 'carcasses') issues = getBookcasesConfigReadinessIssues(cfgForChecks)
      else issues = getClassicConfigReadinessIssues(cfgForChecks)

      // Carcasses legs use fixed leg code and can work without an explicit leg color selection.
      if (type === 'carcasses' && Array.isArray(issues) && issues.length > 0) {
        issues = issues.filter((issue) => issue !== 'missing baseType-color (legs)')
      }

      if (includeIframeCheck && !isConfiguratorLoaded) {
        issues.push('iframe not loaded')
      }
      return issues
    },

    // Expose per-type methods for direct usage/tests
    getDoorConfigReadinessIssues: getDoorConfigReadinessIssues,
    getClassicConfigReadinessIssues: getClassicConfigReadinessIssues,
    getWallUnitConfigReadinessIssues: getWallUnitConfigReadinessIssues,
    getBookcasesConfigReadinessIssues: getBookcasesConfigReadinessIssues,

    // Field-level validation (ignores iframe state)
    validateConfigFields(cfg = {}) {
      const issues = this.getConfigReadinessIssues(cfg, { includeIframeCheck: false })
      return issues.length === 0
    },

    // Alias for saving to URL or other cases that should not depend on iframe state
    isValidConfig(cfg = {}) {
      return this.validateConfigFields(cfg)
    },

    // Full readiness check for posting into iframe (requires iframe loaded)
    isConfigReady(cfg = {}, isConfiguratorLoaded = false) {
      if (!isConfiguratorLoaded) return false
      return this.validateConfigFields(cfg)
    }
  }

  // Expose under the Visualizer namespace
  global.Visualizer = global.Visualizer || {}
  global.Visualizer.getConfigType = global.Visualizer.getConfigType || getConfigTypeSafe
  global.Visualizer.Validation = Validation
})(window);
